import React from "react";
import { useSelector } from "react-redux";
import { useGetLiveTalkingRatioQuery } from "../api/apiSlice";

const POLL_INTERVAL_MS = 10000;

function formatSeconds(totalSeconds) {
  const s = Math.max(0, Math.round(totalSeconds || 0));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function getInitials(name) {
  if (!name) return "?";
  const cleanName = name.replace(/[^a-zA-Z0-9\s]/g, "").trim();
  const parts = cleanName.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return cleanName.substring(0, 2).toUpperCase();
}

export default function TalkingRatio() {
  const meetingId = useSelector((state) => state.meeting.meetingId);
  const organizerId = useSelector((state) => state.meeting.organizerId);
  const { data, error, isLoading, isFetching } = useGetLiveTalkingRatioQuery(
    { meetingId, organizerId },
    {
      skip: !meetingId || !organizerId,
      pollingInterval: POLL_INTERVAL_MS,
      refetchOnMountOrArgChange: true,
    }
  );

  const perParticipant = data?.perParticipant || [];
  const totalSeconds = data?.totalSeconds || 0;

  return (
    <section className="widget-card">
      <h3 className="widget-title" style={{ justifyContent: "space-between" }}>
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="22" />
          </svg>
          Talking Ratio (Live)
        </span>
        {isFetching ? (
          <span className="updating-badge">
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              style={{ animation: "spin 2s linear infinite" }}
            >
              <line x1="12" y1="2" x2="12" y2="6" />
              <line x1="12" y1="18" x2="12" y2="22" />
              <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
              <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
              <line x1="2" y1="12" x2="6" y2="12" />
              <line x1="18" y1="12" x2="22" y2="12" />
              <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
              <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
            </svg>
            Syncing...
          </span>
        ) : (
          <span className="updating-badge" style={{ color: "var(--success)", background: "var(--success-glow)" }}>
            <span
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: "var(--success)",
                display: "inline-block",
                marginRight: "4px",
              }}
            />
            Live
          </span>
        )}
      </h3>

      {isLoading && (
        <div style={{ padding: "16px 0", display: "flex", gap: "10px", flexDirection: "column" }}>
          <div style={{ height: "40px", background: "var(--skeleton-bg)", borderRadius: "8px" }} />
          <div style={{ height: "40px", background: "var(--skeleton-bg)", borderRadius: "8px" }} />
        </div>
      )}

      {error && (
        <div style={{ color: "var(--danger)", fontSize: "0.85rem", padding: "8px 0" }}>
          Failed to load live talking ratio.
        </div>
      )}

      {!isLoading && !error && perParticipant.length === 0 && (
        <div className="no-cards-placeholder">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ marginBottom: "8px", opacity: 0.5 }}
          >
            <line x1="1" y1="1" x2="23" y2="23" />
            <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V5a3 3 0 0 0-5.94-.6" />
            <path d="M17 11.5a7 7 0 0 1-2.12 4.96" />
            <path d="M19 10v1a7.9 7.9 0 0 1-.88 3.64" />
            <line x1="12" y1="19" x2="12" y2="22" />
          </svg>
          <p>No transcript data available yet.</p>
          <p style={{ fontSize: "0.75rem", marginTop: "4px" }}>
            Make sure Teams live transcription is turned on for this meeting.
          </p>
        </div>
      )}

      {perParticipant.length > 0 && (
        <>
          <div className="talking-ratio-summary">
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              Total Voice Time: <strong>{formatSeconds(totalSeconds)}</strong>
            </span>
            <span>Refreshes every {POLL_INTERVAL_MS / 1000}s</span>
          </div>

          <div className="talking-list">
            {perParticipant.map((p) => {
              const initials = getInitials(p.speaker);
              return (
                <div key={p.speaker} className="talking-item">
                  <div className="talking-meta">
                    <div className="speaker-profile">
                      <div className="speaker-avatar">{initials}</div>
                      <span className="speaker-name">{p.speaker}</span>
                    </div>
                    <span className="talking-time">
                      <strong>{p.percent}%</strong> &middot; {formatSeconds(p.secondsSpoken)}
                    </span>
                  </div>
                  <div className="ratio-bar-bg">
                    <div
                      className="ratio-bar-fill"
                      style={{ width: `${p.percent}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Dynamic Keyframes for spin animation inline */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}} />
    </section>
  );
}
